import { useBreakpoint } from '../../../platform/responsive/useBreakpoint'
import { Skeleton } from '../../../platform/async/Skeleton'
import type { AsyncState } from '../../../platform/async/Async'
import { CategoryChip } from '../../../platform/primitives/CategoryChip'
import { DataGrid, type Column, type Group } from '../../../platform/primitives/DataGrid'
import { InlineLink } from '../../../platform/primitives/InlineLink'
import { Money } from '../../../platform/primitives/Money'
import { needsReview, type Txn } from '../../transactions/transactionWire'
import { shortDay } from '../overviewFormat'
import { OverviewWidget } from '../OverviewWidget'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { SegmentedControl, type Segment } from '../../../platform/primitives/SegmentedControl'

/**
 * Recent transactions: M6's most recent five. The Owner column is `ownerName`, the
 * EFFECTIVE owner (`effective_beneficial_owner`; `ownerId` is the override and is
 * not read). The header pills filter those five rows in the browser — no request.
 * Amounts keep M6's own sign handling. The row link is the literal `/transactions/:id`
 * M6 itself uses; importing App's constant would be a cycle.
 */
type Filter = 'all' | 'review' | `owner:${string}`

const keeps = (f: Filter, t: Txn): boolean =>
  f === 'all' ? true : f === 'review' ? needsReview(t) : t.effectiveOwnerId === f.slice('owner:'.length)

export function RecentTransactionsWidget({
  state,
  categories,
  members,
}: {
  state: AsyncState<Txn[]>
  categories: ReadonlyMap<string, string>
  /** The household members, one pill each: the id filters, the name labels. */
  members: readonly { id: string; name: string }[]
}) {
  const navigate = useNavigate()
  const [filter, setFilter] = useState<Filter>('all')
  const segments: Segment<Filter>[] = [
    { value: 'all', label: 'All' },
    { value: 'review', label: 'Review' },
    ...members.map((m): Segment<Filter> => ({ value: `owner:${m.id}`, label: m.name })),
  ]
  // Four columns only where a half-width tile is wide enough for them (1440 and up). Narrower, the category
  // sits under the merchant instead of in a column of its own, where a long chip would run over its
  // neighbours. A tile's width follows the viewport (two columns above the phone), so this is the one breakpoint.
  const bp = useBreakpoint()
  const stacked = bp !== 'wide'
  const phone = bp === 'phone'

  const categoryLabel = (t: Txn): string | undefined => (t.categoryId === null ? 'Needs category' : categories.get(t.categoryId))

  const category = (t: Txn) => {
    const name = t.categoryId === null ? undefined : categories.get(t.categoryId)
    // "Needs category" is the row's own state, in words; an id this client has no name for yet reads a dash.
    return t.categoryId === null ? (
      <span className="ov-chip ov-chip--warn">Needs category</span>
    ) : name === undefined ? (
      <span className="note">—</span>
    ) : (
      <CategoryChip name={name} size="chip" />
    )
  }

  const columns: Column<Txn>[] = [
    {
      key: 'merchant',
      header: 'Merchant',
      width: 'minmax(0, 1.6fr)',
      render: (t) => (
        <span className={stacked ? 'ov-txn__merchant ov-txn__merchant--stacked' : 'ov-txn__merchant'}>
          <span className="ov-txn__line">
            <span
              className={`ov-txn__dot${t.amountCents > 0 ? ' ov-txn__dot--income' : t.categoryId === null ? ' ov-txn__dot--uncategorised' : ''}`}
              aria-hidden="true"
            />
            <InlineLink to={`/transactions/${t.id}`} className="ov-txn__link" title={t.merchant}>
              {/* Its own block: only a block's text takes an ellipsis, not a flex link's anonymous item. */}
              <span className="ov-txn__name">{t.merchant}</span>
            </InlineLink>
            {t.isPending && <span className="ov-chip">Pending</span>}
          </span>
          {stacked && <span className="ov-txn__category" title={categoryLabel(t)}>{category(t)}</span>}
        </span>
      ),
    },
    ...(stacked
      ? []
      : [{ key: 'category', header: 'Category', width: 'minmax(0, 1.2fr)', render: (t: Txn) => <span className="ov-txn__category" title={categoryLabel(t)}>{category(t)}</span> }]),
    stacked
      ? { key: 'date', header: 'Date', width: '56px', render: (t: Txn) => <span className="note">{shortDay(t.date)}</span> }
      : { key: 'owner', header: 'Owner', width: 'minmax(0, 0.8fr)', render: (t: Txn) => <span className="note">{t.ownerName ?? '—'}</span> },
    {
      key: 'amount',
      header: 'Amount',
      width: stacked ? 'minmax(0, 0.9fr)' : '110px',
      align: 'end',
      render: (t) => <Money cents={t.amountCents} currency={t.currency} kind="flow" />,
    },
  ]

  return (
    <OverviewWidget<Txn[]>
      title="Recent transactions"
      subtitle="Latest five"
      headExtra={
        <div className={phone ? 'ov-widget__extra ov-widget__extra--phone' : 'ov-widget__extra'}>
        <SegmentedControl<Filter>
          label="Filter recent transactions"
          name="recent-filter"
          options={segments}
          value={filter}
          onChange={setFilter}
        />
        </div>
      }
      href="/transactions"
      surface="overview-recent"
      state={state}
      skeleton={
        <>
          <Skeleton height={32} />
          <Skeleton height={32} />
          <Skeleton height={32} />
        </>
      }
      empty={{ title: 'No transactions yet', action: { label: 'See accounts', onAct: () => void navigate('/accounts') } }}
    >
      {(rows) => {
        const groups: Group<Txn>[] = [{ label: '', rows: rows.filter((t) => keeps(filter, t)) }]
        return (
          <>
            <div className="ov-recent__grid">
            <DataGrid<Txn>
              label="Recent transactions"
              columns={columns}
              groups={groups}
              rowKey={(t) => t.id}
              // The grid's own empty is a pill that matched nothing among the five.
              empty={<span className="note">Nothing matches.</span>}
            />
            </div>
            <InlineLink to="/transactions" className="ov-viewall">
              View all
            </InlineLink>
          </>
        )
      }}
    </OverviewWidget>
  )
}
